import type { GameStateData } from '../game/GameState.js';
import { tileKey, type Pip, type Tile } from '../game/types.js';
import { openPipValues } from './SuitTracker.js';

/**
 * Stateful Bayesian-lite belief over hidden tile locations from a single
 * AI's perspective. For every tile the AI cannot see directly (i.e. not in
 * its own hand and not played on the chain), maintain a probability
 * distribution over the set of locations:
 *
 *   [opponent_seat_0, opponent_seat_1, ..., opponent_seat_{N-1}, BONEYARD]
 *
 * Note that `opponent_seat_{viewSeat}` is always 0 (the AI knows its own
 * hand). The per-tile distribution sums to 1.
 *
 * Updates are applied at the *engine reducer* boundary by the caller (the
 * Hard-tier strategy / ISMCTS runner). The tracker is stateless w.r.t. the
 * engine state — it just folds in observations.
 *
 * Hard tier (M7) replaces the stateless [[passconstraints]] derivation with
 * this proper Bayesian belief tracker carried across plays.
 */

export const BONEYARD = -1;

interface TileBelief {
  /** weight per location: index ∈ [0, playerCount) = seat, BONEYARD (-1) = boneyard. */
  readonly perSeat: number[];
  /** boneyard weight in [0,1]. */
  boneyardWeight: number;
}

export class BeliefTracker {
  private readonly playerCount: number;
  private readonly viewSeat: number;
  private readonly beliefs = new Map<string, TileBelief>();
  private boneyardSize: number;

  constructor(state: GameStateData, viewSeat: number) {
    this.playerCount = state.playerCount;
    this.viewSeat = viewSeat;
    this.boneyardSize = state.boneyard.length;
    const unseen = computeUnseen(state, viewSeat);
    const opponentSeats: number[] = [];
    let totalUnknownInHands = 0;
    for (let s = 0; s < this.playerCount; s += 1) {
      if (s === viewSeat) continue;
      opponentSeats.push(s);
      totalUnknownInHands += state.hands[s]?.length ?? 0;
    }
    const total = totalUnknownInHands + state.boneyard.length;
    if (total === 0) return;

    for (const tile of unseen) {
      const perSeat: number[] = new Array<number>(this.playerCount).fill(0);
      for (const s of opponentSeats) {
        const handSize = state.hands[s]?.length ?? 0;
        perSeat[s] = handSize / total;
      }
      const boneyardWeight = state.boneyard.length / total;
      this.beliefs.set(tileKey(tile), { perSeat, boneyardWeight });
    }
    // Replay the same Bayesian-lite inference [[passconstraints]] applies:
    // every seat in the trailing consecutive-pass window provably lacked
    // tiles touching the current open pips at the time they passed.
    const passes = state.consecutivePasses;
    if (passes > 0) {
      const openPips = openPipValues(state);
      if (openPips.length > 0) {
        for (let k = 0; k < passes; k += 1) {
          const seat =
            (state.currentSeat - k - 1 + this.playerCount * (k + 1)) % this.playerCount;
          if (seat !== viewSeat) this.observePass(seat, openPips);
        }
      }
    }
  }

  /** Probability that `tile` is in `seat`'s hand (or BONEYARD for boneyard). */
  pInLocation(tile: Tile, location: number): number {
    const belief = this.beliefs.get(tileKey(tile));
    if (!belief) return 0;
    if (location === BONEYARD) return belief.boneyardWeight;
    return belief.perSeat[location] ?? 0;
  }

  /** Iterate over all unseen tiles still tracked. */
  unseenTiles(): readonly Tile[] {
    const out: Tile[] = [];
    for (const key of this.beliefs.keys()) {
      const [a, b] = key.split('-').map(Number) as [Pip, Pip];
      out.push([a, b]);
    }
    return out;
  }

  getBoneyardSize(): number {
    return this.boneyardSize;
  }

  /**
   * Tile `tile` was just played from `seat`. Remove it from the tracker —
   * it's now public information on the chain.
   */
  observePlay(_seat: number, tile: Tile): void {
    this.beliefs.delete(tileKey(tile));
  }

  /**
   * `seat` drew one unknown tile from the boneyard. For every tile that
   * might still be in the boneyard, shift `P(boneyard) / boneyardSize` mass
   * to that seat. Boneyard size decreases by 1.
   */
  observeDraw(seat: number): void {
    if (this.boneyardSize <= 0) return;
    if (seat === this.viewSeat) return;
    const size = this.boneyardSize;
    for (const belief of this.beliefs.values()) {
      const moved = belief.boneyardWeight / size;
      belief.perSeat[seat] = (belief.perSeat[seat] ?? 0) + moved;
      belief.boneyardWeight = belief.boneyardWeight * (size - 1) / size;
    }
    this.boneyardSize -= 1;
  }

  /**
   * `seat` passed. Under `draw-until-playable` (All Fives default) a pass
   * implies the boneyard was empty at the time AND `seat` holds no tile
   * matching any current open pip. Zero out P(t ∈ seat) for any tile
   * touching `openPips`; renormalize.
   *
   * Under other draw rules, the inference is weaker — pass could mean the
   * seat just drew one tile and still couldn't play. We still apply the
   * suit-matching zero since the seat's *current* hand is provably devoid
   * of the open pips at this moment.
   */
  observePass(seat: number, openPips: readonly Pip[]): void {
    if (seat === this.viewSeat) return;
    if (openPips.length === 0) return;
    const blocked = new Set<Pip>(openPips);
    for (const [key, belief] of this.beliefs.entries()) {
      const [a, b] = key.split('-').map(Number) as [Pip, Pip];
      if (!blocked.has(a) && !blocked.has(b)) continue;
      const removed = belief.perSeat[seat] ?? 0;
      if (removed === 0) continue;
      belief.perSeat[seat] = 0;
      // Renormalize over remaining (seats + boneyard) so the tile-level mass
      // stays at 1. Mass to redistribute = `removed`.
      let denominator = belief.boneyardWeight;
      for (let s = 0; s < this.playerCount; s += 1) {
        if (s === this.viewSeat || s === seat) continue;
        denominator += belief.perSeat[s] ?? 0;
      }
      if (denominator <= 0) {
        // Degenerate: every other location was 0 too. Spread uniformly.
        const opponents = this.playerCount - 1;
        const share = removed / Math.max(1, opponents);
        for (let s = 0; s < this.playerCount; s += 1) {
          if (s === this.viewSeat || s === seat) continue;
          belief.perSeat[s] = (belief.perSeat[s] ?? 0) + share;
        }
        continue;
      }
      const scale = (denominator + removed) / denominator;
      belief.boneyardWeight *= scale;
      for (let s = 0; s < this.playerCount; s += 1) {
        if (s === this.viewSeat || s === seat) continue;
        belief.perSeat[s] = (belief.perSeat[s] ?? 0) * scale;
      }
    }
  }
}

function computeUnseen(state: GameStateData, viewSeat: number): readonly Tile[] {
  const seenKeys = new Set<string>();
  for (const tile of state.hands[viewSeat] ?? []) seenKeys.add(tileKey(tile));
  for (const arm of state.chain.arms) {
    for (const tile of arm.tiles) seenKeys.add(tileKey(tile));
  }
  const out: Tile[] = [];
  for (let a = 0; a <= 6; a += 1) {
    for (let b = a; b <= 6; b += 1) {
      const key = `${a}-${b}`;
      if (!seenKeys.has(key)) out.push([a as Pip, b as Pip]);
    }
  }
  return out;
}
